import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--liturgical-primary)] hover:underline mb-8 font-['Nunito']">
          <ArrowLeft className="w-4 h-4" /> Back to Parish
        </Link>
        
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E0D5] p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-[var(--liturgical-primary)]" />
            <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26]">Privacy Policy</h1>
          </div>
          
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-8">Last Updated: February 25, 2026</p>
          
          <div className="prose prose-slate max-w-none font-['Nunito'] text-[#2D2A26]">
            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">1. Introduction</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Welcome to Parish ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                Parish is a social marketplace platform designed for Catholic communities, enabling members to connect with their parish, participate in community activities, and engage in local commerce. By using Parish, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">2. Information We Collect</h2>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password, profile picture, and cover image</li>
                <li><strong>Profile Information:</strong> Bio, religious role, confirmation saint, relationship status, favorite quote, and social media links</li>
                <li><strong>Parish Membership:</strong> Parish affiliation, diocese, and membership status</li>
                <li><strong>Location Data:</strong> Zip/postal code and country (for marketplace distance filtering)</li>
                <li><strong>Marketplace Listings:</strong> Item descriptions, prices, photos, and location information</li>
                <li><strong>Communications:</strong> Messages sent through our platform, posts, comments, and reviews</li>
                <li><strong>Religious Information:</strong> Sacrament preferences, Mass attendance interests, and religious role within your parish</li>
              </ul>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
                <li><strong>Log Data:</strong> IP address, access times, referring URLs</li>
                <li><strong>Cookies:</strong> Session cookies and analytics cookies (see our <Link to="/cookies" className="text-[var(--liturgical-primary)] hover:underline">Cookie Policy</Link>)</li>
              </ul>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">2.3 Information from Third Parties</h3>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Google Sign-In:</strong> If you choose to sign in with Google, we receive your name, email, and profile picture from Google</li>
                <li><strong>Parish Administrators:</strong> Your parish admin may verify or update your membership status</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">3. How We Use Your Information</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Create and manage your account</li>
                <li>Connect you with your parish community</li>
                <li>Enable marketplace features, including distance-based filtering</li>
                <li>Facilitate communication between members</li>
                <li>Display relevant content and recommendations</li>
                <li>Send notifications about community activities, messages, and updates</li>
                <li>Provide the Sacrament Finder feature to help you locate Mass, Reconciliation, and Adoration times</li>
                <li>Improve our platform and develop new features</li>
                <li>Ensure safety and security of our community</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">4. How We Share Your Information</h2>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">4.1 With Other Users</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Your profile information, posts, marketplace listings, and reviews are visible to other Parish users according to your privacy settings. Your name and profile picture are visible when you interact with others.
              </p>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">4.2 With Service Providers</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-2">We share information with third-party service providers who assist us in operating our platform:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Cloudinary:</strong> For image and media storage</li>
                <li><strong>PostHog:</strong> For analytics and platform improvement</li>
                <li><strong>OpenStreetMap (Nominatim):</strong> For geocoding location data</li>
                <li><strong>SendGrid:</strong> For email communications</li>
              </ul>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">4.3 With Parish Administrators</h3>
              <p className="text-[#4A4A4A] leading-relaxed">
                Parish administrators can view member information for their parish to manage membership, verify users, and coordinate community activities.
              </p>

              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">4.4 For Legal Reasons</h3>
              <p className="text-[#4A4A4A] leading-relaxed">
                We may disclose information if required by law, to protect our rights, to ensure user safety, or to respond to legal processes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">5. Data Retention</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account and associated data at any time.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                Some information may be retained for longer periods for legal compliance, dispute resolution, or to enforce our agreements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">6. Your Rights and Choices</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Withdraw Consent:</strong> Where processing is based on consent</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                To exercise these rights, please contact us at <a href="mailto:privacy@parish.com" className="text-[var(--liturgical-primary)] hover:underline">privacy@parish.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">7. Data Security</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">8. Children's Privacy</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Parish is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">9. International Data Transfers</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We take appropriate safeguards to ensure your information remains protected.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">10. Changes to This Policy</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of Parish after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">11. Contact Us</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                If you have questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-[#F9F7F2] rounded-lg p-4 mt-4">
                <p className="text-[#4A4A4A]"><strong>Email:</strong> <a href="mailto:privacy@parish.com" className="text-[var(--liturgical-primary)] hover:underline">privacy@parish.com</a></p>
              </div>
            </section>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            See also: <Link to="/terms" className="text-[var(--liturgical-primary)] hover:underline">Terms of Service</Link> · <Link to="/cookies" className="text-[var(--liturgical-primary)] hover:underline">Cookie Policy</Link> · <Link to="/community-guidelines" className="text-[var(--liturgical-primary)] hover:underline">Community Guidelines</Link> · <Link to="/marketplace-terms" className="text-[var(--liturgical-primary)] hover:underline">Marketplace Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
